import {
  Component,
  Directive,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  OnInit,
  OnDestroy,
  Injector,
  Renderer2,
  ComponentRef,
  ElementRef,
  TemplateRef,
  ViewContainerRef,
  ComponentFactoryResolver,
  NgZone
} from '@angular/core';
import {listenToTriggers} from '@ng-bootstrap/ng-bootstrap/util/triggers';
import {positionElements, Placement, PlacementArray} from './KngBubbleTipPositioning';
import {PopupService} from '@ng-bootstrap/ng-bootstrap/util/popup';
import {NgbTooltipConfig} from '@ng-bootstrap/ng-bootstrap/tooltip/tooltip-config';

let nextId = 0;

@Component({
  selector: 'kng-bubble-tip-window',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class]': '"kng-bubble-tip show kng-bubble-tip-" + placement.split("-")[0]+" kng-bubble-tip-" + placement',
    'role': 'tooltip',
    '[id]': 'id'
  },
  templateUrl: './kng-bubble-tip-window.component.html'
})
export class KngBubbleTipWindow {
  @Input() placement: Placement = 'top';
  @Input() id: string;

  constructor(private _element: ElementRef, private _renderer: Renderer2) {}

  applyPlacement(_placement: Placement) {
    // remove the current placement classes
    this._renderer.removeClass(this._element.nativeElement, 'kng-bubble-tip-' + this.placement.toString().split('-')[0]);
    this._renderer.removeClass(this._element.nativeElement, 'kng-bubble-tip-' + this.placement.toString());

    // set the new placement classes
    this.placement = _placement;

    // apply the new placement
    this._renderer.addClass(this._element.nativeElement, 'kng-bubble-tip-' + this.placement.toString().split('-')[0]);
    this._renderer.addClass(this._element.nativeElement, 'kng-bubble-tip-' + this.placement.toString());
  }
}

/**
 * A lightweight, extensible directive for fancy tooltip creation.
 */
@Directive({selector: '[kngBubbleTip]', exportAs: 'kngBubbleTip'})
export class KngBubbleTipComponent implements OnInit, OnDestroy {
  /**
   * Placement of a popover accepts:
   *    "top", "top-left", "top-right", "bottom", "bottom-left", "bottom-right",
   *    "left", "left-top", "left-bottom", "right", "right-top", "right-bottom"
   * and array of above values.
   */
  @Input() placement: PlacementArray;
  /**
   * Specifies events that should trigger. Supports a space separated list of event names.
   */
  @Input() triggers: string;
  /**
   * A selector specifying the element the tooltip should be appended to.
   * Currently only supports "body".
   */
  @Input() container: string;
  /**
   * A flag indicating if a given tooltip is disabled and should not be displayed.
   *
   * @since 1.1.0
   */
  @Input() disableTooltip: boolean;
  /**
   * Emits an event when the tooltip is shown
   */
  @Output() shown = new EventEmitter();
  /**
   * Emits an event when the tooltip is hidden
   */
  @Output() hidden = new EventEmitter();

  private _kngBubbleTip: string | TemplateRef<any>;
  private _kngBubbleTipWindowId = `kng-bubble-tip-${nextId++}`;
  private _popupService: PopupService<KngBubbleTipWindow>;
  private _windowRef: ComponentRef<KngBubbleTipWindow>;
  private _unregisterListenersFn;
  private _zoneSubscription: any;

  constructor(
    private _elementRef: ElementRef, private _renderer: Renderer2, injector: Injector,
    componentFactoryResolver: ComponentFactoryResolver, viewContainerRef: ViewContainerRef, config: NgbTooltipConfig,
    ngZone: NgZone) {
    this.placement = config.placement;
    this.triggers = config.triggers;
    this.container = config.container;
    // this.disableTooltip = config.disableTooltip;
    this._popupService = new PopupService<KngBubbleTipWindow>(
      KngBubbleTipWindow, injector, viewContainerRef, _renderer, componentFactoryResolver);

    this._zoneSubscription = ngZone.onStable.subscribe(() => {
      if (this._windowRef) {
        this._windowRef.instance.applyPlacement(
          positionElements(
            this._elementRef.nativeElement, this._windowRef.location.nativeElement, this.placement,
            this.container === 'body'));
      }
    });
  }

  /**
   * Content to be displayed as tooltip. If falsy, the tooltip won't open.
   */
  @Input()
  set kngBubbleTip(value: string | TemplateRef<any>) {
    this._kngBubbleTip = value;
    if (!value && this._windowRef) {
      this.close();
    }
  }

  get kngBubbleTip() { return this._kngBubbleTip; }

  /**
   * Opens an element’s tooltip. This is considered a “manual” triggering of the tooltip.
   * The context is an optional value to be injected into the tooltip template when it is created.
   */
  open(context?: any) {
    if (!this._windowRef && this._kngBubbleTip && !this.disableTooltip) {
      this._windowRef = this._popupService.open(this._kngBubbleTip, context);
      this._windowRef.instance.id = this._kngBubbleTipWindowId;

      this._renderer.setAttribute(this._elementRef.nativeElement, 'aria-describedby', this._kngBubbleTipWindowId);

      if (this.container === 'body') {
        window.document.querySelector(this.container).appendChild(this._windowRef.location.nativeElement);
      }

      this._windowRef.instance.placement = Array.isArray(this.placement) ? this.placement[0] : this.placement;

      // apply styling to set basic css-classes on target element, before going for positioning
      this._windowRef.changeDetectorRef.detectChanges();
      this._windowRef.changeDetectorRef.markForCheck();

      // position tooltip along the element
      this._windowRef.instance.applyPlacement(
        positionElements(
          this._elementRef.nativeElement, this._windowRef.location.nativeElement, this.placement,
          this.container === 'body'));

      this.shown.emit();
    }
  }

  /**
   * Closes an element’s tooltip. This is considered a “manual” triggering of the tooltip.
   */
  close(): void {
    if (this._windowRef != null) {
      this._renderer.removeAttribute(this._elementRef.nativeElement, 'aria-describedby');
      this._popupService.close();
      this._windowRef = null;
      this.hidden.emit();
    }
  }

  /**
   * Toggles an element’s tooltip. This is considered a “manual” triggering of the tooltip.
   */
  toggle(): void {
    if (this._windowRef) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Returns whether or not the tooltip is currently being shown
   */
  isOpen(): boolean { return this._windowRef != null; }

  ngOnInit() {
    this._unregisterListenersFn = listenToTriggers(
      this._renderer, this._elementRef.nativeElement, this.triggers, this.open.bind(this), this.close.bind(this),
      this.toggle.bind(this));
  }

  ngOnDestroy() {
    this.close();
    // This check is needed as it might happen that ngOnDestroy is called before ngOnInit
    // under certain conditions, see: https://github.com/ng-bootstrap/ng-bootstrap/issues/2199
    if (this._unregisterListenersFn) {
      this._unregisterListenersFn();
    }
    this._zoneSubscription.unsubscribe();
  }
}



// WEBPACK FOOTER //
// D:/dclean-web-latest/cot-web/src/app/layout/kingswell/kng-bubble-tip/kng-bubble-tip.component.ts